import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Loader2, Wand2, Dice5, Sparkles, AlertTriangle, Camera, Quote,
  BookMarked, Save, Trash2, ChevronDown, Link2, RotateCw, Eye, EyeOff,
  CalendarPlus,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import api from '../../lib/api';
import { DemoDirectorInsights } from './DemoDirectorInsights';

const PILLAR_LINE_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

/**
 * Demo Story Director — super-admin tool to dial Acme's narrative arc
 * (dip severity, recovery pace, primary-constraint pillar, industry preset,
 * final maturity band, RNG seed) before each prospect demo. Live preview
 * sparklines on the right; Apply on the left.
 */
export const DemoDirector = ({ open, onOpenChange, onApplied, batchReengageIds, onBatchReengageConsumed }) => {
  const [catalog, setCatalog] = useState(null);
  const [config, setConfig] = useState({
    dip_severity: 'moderate',
    recovery_pace: 'moderate',
    primary_constraint_pillar: 'P5',
    industry_preset: 'manufacturing',
    final_band_target: 'Managed',
    seed: 42,
    // Knob v2 (Feb 2026) — backwards-compatible defaults
    constraint_intensity: 'standard',
    benchmark_percentile: 'p75',
  });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);

  // Tagline (GPT-5.2) state
  const [tagline, setTagline] = useState(null);
  const [taglineLoading, setTaglineLoading] = useState(false);

  // Ref to the preview panel — used by html2canvas for the cover-slide PNG
  const previewPanelRef = useRef(null);
  const [capturing, setCapturing] = useState(false);

  // Saved story templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', description: '', tags: '' });
  const [saving, setSaving] = useState(false);

  // Insights tab state — aggregate analytics across all templates
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  // Auto-switch to Insights tab when the digest deep link arrives
  // with batch re-engage IDs. The Insights component will then pick
  // them up via its own effect and seed the batch panel.
  useEffect(() => {
    if (open && batchReengageIds && batchReengageIds.length > 0) {
      setActiveTab('insights');
    }
  }, [open, batchReengageIds]);

  // Confirmation dialog state — replaces window.confirm for delete/revoke
  // and the Apply-to-Acme step. Single dialog, swapped via `confirmAction`.
  const [confirmAction, setConfirmAction] = useState(null);
  // confirmAction shape: { kind: 'delete'|'revoke'|'apply', template?, title, description, confirmLabel, onConfirm, danger?: boolean }

  /* ---- Load option catalog once on open ---- */
  useEffect(() => {
    if (!open || catalog) return;
    api.get('/admin/demo-director/catalog')
      .then((r) => setCatalog(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Could not load catalog'));
  }, [open, catalog]);

  /* ---- Load saved templates whenever the modal opens ---- */
  const refreshTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await api.get('/admin/demo-director/templates');
      setTemplates(res.data?.templates || []);
    } catch (err) {
      // Non-fatal — modal still works without templates
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(detail);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) refreshTemplates();
  }, [open, refreshTemplates]);

  /* ---- Insights tab fetch (only when activated) ---- */
  const refreshInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const res = await api.get('/admin/demo-director/insights');
      setInsights(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(detail);
    } finally {
      setInsightsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open && activeTab === 'insights') refreshInsights();
  }, [open, activeTab, refreshInsights]);

  /* ---- Auto-refresh preview whenever config changes (debounced) ---- */
  const refreshPreview = useCallback(async (cfg) => {
    setLoadingPreview(true);
    try {
      const res = await api.post('/admin/demo-director/preview', cfg);
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => refreshPreview(config), 220);
    return () => clearTimeout(t);
  }, [open, config, refreshPreview]);

  // Knob change invalidates any previously-generated tagline since it
  // was anchored on the old preview numbers.
  useEffect(() => { setTagline(null); }, [config]);

  /* ---- Handlers ---- */
  const setKnob = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const reroll = () => setKnob('seed', Math.floor(Math.random() * 9_999_999));

  const generateTagline = async () => {
    setTaglineLoading(true);
    try {
      const res = await api.post('/admin/demo-director/tagline', config);
      setTagline(res.data);
      toast.success('Cover-slide copy generated', {
        description: `${res.data.model} · ${res.data.headline_kpis?.length || 0} headline KPIs`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Tagline generation failed');
    } finally {
      setTaglineLoading(false);
    }
  };

  const captureCoverSlide = async () => {
    if (!previewPanelRef.current) return;
    setCapturing(true);
    try {
      const canvas = await html2canvas(previewPanelRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('PNG capture failed');
          return;
        }
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
        const slug = `${config.industry_preset}-${config.final_band_target.toLowerCase()}-${config.seed}`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `acme-cover-${slug}-${stamp}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Cover-slide PNG downloaded');
      }, 'image/png');
    } catch (err) {
      toast.error('Capture failed: ' + (err?.message || 'unknown'));
    } finally {
      setCapturing(false);
    }
  };

  const saveTemplate = async () => {
    const name = saveForm.name.trim();
    if (name.length < 2) {
      toast.error('Give the template a name first');
      return;
    }
    setSaving(true);
    try {
      const tags = saveForm.tags
        ? saveForm.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      const res = await api.post('/admin/demo-director/templates', {
        name,
        description: saveForm.description || '',
        tags,
        config,
        tagline: tagline || null,
      });
      setTemplates((prev) => [res.data, ...prev]);
      setSaveForm({ name: '', description: '', tags: '' });
      setSaveOpen(false);
      toast.success(`Template saved: ${res.data.name}`, {
        description: tagline ? 'Includes cover-slide tagline' : 'Knobs only',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadTemplate = async (t) => {
    setConfig(t.config);
    if (t.tagline) {
      // Briefly defer so the config-change effect doesn't immediately
      // null the tagline we just loaded.
      setTimeout(() => setTagline(t.tagline), 50);
    }
    try {
      await api.post(`/admin/demo-director/templates/${t.id}/touch`);
    } catch (_e) { /* non-fatal */ }
    toast.success(`Loaded: ${t.name}`);
  };

  const deleteTemplate = async (t) => {
    setConfirmAction({
      kind: 'delete',
      template: t,
      title: `Delete template "${t.name}"?`,
      description: 'This permanently removes the saved knobs + tagline. The action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/demo-director/templates/${t.id}`);
          setTemplates((prev) => prev.filter((x) => x.id !== t.id));
          toast.success('Template deleted');
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  const copyShareUrl = async (t) => {
    try {
      const res = await api.post(`/admin/demo-director/templates/${t.id}/share-url`);
      // Backend returns a relative `path`; assemble against the user's
      // current origin so the URL is always reachable from where they are.
      const fullUrl = `${window.location.origin}${res.data.path}`;
      await navigator.clipboard.writeText(fullUrl);
      // Optimistically mark the template as shared so the analytics chip
      // flips from "Not shared" → "Share URL active" without waiting for
      // a refetch.
      setTemplates((prev) => prev.map((row) =>
        row.id === t.id ? { ...row, share_token: res.data.share_token } : row,
      ));
      toast.success('Public demo URL copied', {
        description: 'Paste into a calendar invite — opens a fully-applied Acme demo for the prospect.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate share URL');
    }
  };

  const revokeShareUrl = async (t) => {
    setConfirmAction({
      kind: 'revoke',
      template: t,
      title: `Revoke public demo URL for "${t.name}"?`,
      description: 'Any calendar invites or share links already sent will stop working. You can mint a fresh URL afterwards (which will rotate the token).',
      confirmLabel: 'Revoke',
      danger: false,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/demo-director/templates/${t.id}/share-url`);
          setTemplates((prev) => prev.map((row) =>
            row.id === t.id
              ? { ...row, share_token: null, share_open_count: 0, last_share_open_at: null }
              : row,
          ));
          toast.success('Share URL revoked');
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Revoke failed');
        }
      },
    });
  };

  const downloadCalendarInvite = async (t) => {
    try {
      // Mint (or fetch) the share URL — same idempotent endpoint as Share button.
      const res = await api.post(`/admin/demo-director/templates/${t.id}/share-url`);
      const shareUrl = `${window.location.origin}${res.data.path}`;
      // Optimistic chip flip so the row immediately reflects "Share URL active".
      setTemplates((prev) => prev.map((row) =>
        row.id === t.id ? { ...row, share_token: res.data.share_token } : row,
      ));

      // Compose the .ics body. Use the tagline first line as subject, full
      // tagline + share URL as description. Schedule for tomorrow at 10:00
      // local-equivalent (UTC) for a 1h duration — rep will adjust.
      const subject = t.tagline?.tagline?.split('\n')[0]?.trim() || `Acme demo — ${t.name}`;
      const subheadline = t.tagline?.subheadline || '';
      const headlineKpis = (t.tagline?.headline_kpis || [])
        .map((k) => `• ${k.label}: ${k.value}${k.delta ? ` (${k.delta})` : ''}`)
        .join('\\n');
      const description = [
        t.tagline?.tagline || '',
        subheadline,
        '',
        'Live demo URL (opens directly into Acme — no login required):',
        shareUrl,
        '',
        headlineKpis ? 'Headline KPIs:' : '',
        headlineKpis,
      ].filter(Boolean).join('\\n');

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      const end = new Date(tomorrow.getTime() + 60 * 60 * 1000);
      const fmtICS = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const uid = `${t.id}-${Date.now()}@acme-demo`;

      const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Seertech//Demo Story Director//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${fmtICS(new Date())}`,
        `DTSTART:${fmtICS(tomorrow)}`,
        `DTEND:${fmtICS(end)}`,
        `SUMMARY:${subject.replace(/\n/g, ' ')}`,
        `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
        `URL:${shareUrl}`,
        'STATUS:TENTATIVE',
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\r\n');

      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `acme-demo-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Calendar invite downloaded', {
        description: 'Opens in your default calendar app — adjust attendees + time, then send.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate invite');
    }
  };

  const apply = async () => {
    setConfirmAction({
      kind: 'apply',
      title: 'Apply this story to Acme Industries?',
      description: (
        `Industry: ${preview?.headline?.industry}\n` +
        `Final band: ${preview?.headline?.final_band} (${preview?.headline?.final_maturity_score})\n` +
        `Constraint: ${preview?.headline?.primary_constraint_pillar_label}\n\n` +
        `LEI baselines, KPI actuals, and Maturity history will be wiped and re-written.`
      ),
      confirmLabel: 'Apply story',
      danger: true,
      onConfirm: async () => {
        setApplying(true);
        try {
          const res = await api.post('/admin/demo-director/apply', config);
          toast.success(`Applied story to Acme — ${res.data.inserted} rows written`, {
            description: `${preview?.headline?.industry} · ${preview?.headline?.final_band} · LEI ${preview?.headline?.final_lei_avg}`,
          });
          onApplied?.(res.data);
          onOpenChange?.(false);
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Apply failed');
        } finally {
          setApplying(false);
        }
      },
    });
  };

  /* ---- Derived chart data ---- */
  const leiChartData = useMemo(() => {
    if (!preview) return [];
    return preview.periods.map((p, i) => ({
      period: p.slice(2),
      avg: preview.headline.lei_overall_sparkline[i],
    }));
  }, [preview]);

  const maturityChartData = useMemo(() => {
    if (!preview) return [];
    return [...preview.maturity_trend].sort((a, b) => a.snap_idx - b.snap_idx).map((m) => {
      const d = new Date(m.created_at);
      return {
        date: `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`,
        overall: m.overall_score,
      };
    });
  }, [preview]);

  const kpiChartData = useMemo(() => {
    if (!preview) return [];
    return preview.periods.map((p, i) => {
      const row = { period: p.slice(2) };
      preview.kpi_trends.forEach((k) => {
        // Normalise to 0-100 vs baseline so 7 KPIs fit one chart.
        const baseline = k.baseline || 1;
        const ratio = (k.values[i] / baseline) * 100;
        row[k.kpi_id] = Math.round(ratio * 10) / 10;
      });
      return row;
    });
  }, [preview]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl p-0 gap-0"
        data-testid="demo-director-dialog"
      >
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                <Wand2 className="h-4.5 w-4.5 text-fuchsia-500" />
                Demo Story Director
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-1">
                Dial Acme's narrative arc, preview the trendlines, then apply to the demo tenant.
                Affects LEI baselines, KPI actuals, and Maturity history only — cohorts/programs/business cases stay intact.
              </DialogDescription>
            </div>
            {/* Templates picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs flex-none mr-6"
                  data-testid="dd-templates-trigger"
                >
                  <BookMarked className="h-3.5 w-3.5 text-fuchsia-500" />
                  Templates
                  {templates.length > 0 && (
                    <span className="rounded-full bg-fuchsia-100 px-1.5 text-[10px] font-semibold text-fuchsia-700">
                      {templates.length}
                    </span>
                  )}
                  <ChevronDown className="h-3 w-3 text-slate-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-80 max-h-96 overflow-y-auto p-2"
                data-testid="dd-templates-popover"
              >
                {templatesLoading && (
                  <div className="flex items-center gap-2 p-2 text-[11px] text-slate-500">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </div>
                )}
                {!templatesLoading && templates.length === 0 && (
                  <div className="p-3 text-[11px] text-slate-500 italic">
                    No saved templates yet. Dial knobs, optionally generate a tagline, then click <span className="font-semibold">Save template…</span>
                  </div>
                )}
                {!templatesLoading && templates.map((t) => (
                  <div
                    key={t.id}
                    className="group flex items-start gap-1.5 rounded-md border border-slate-100 bg-white px-2 py-1.5 mb-1 last:mb-0 hover:border-fuchsia-200 hover:bg-fuchsia-50/40 transition-colors"
                    data-testid={`dd-template-row-${t.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => loadTemplate(t)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`dd-template-load-${t.id}`}
                      title="Load this template"
                    >
                      <div className="text-[11px] font-semibold text-slate-800 truncate">
                        {t.name}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5 text-[10px] text-slate-500 capitalize">
                        <span>{t.config?.industry_preset}</span>
                        <span>·</span>
                        <span>{t.config?.final_band_target}</span>
                        <span>·</span>
                        <span>seed {t.config?.seed}</span>
                        {t.use_count > 0 && (
                          <>
                            <span>·</span>
                            <span>{t.use_count}× used</span>
                          </>
                        )}
                      </div>
                      {t.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {t.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {t.description && (
                        <div className="mt-1 text-[10px] text-slate-500 line-clamp-2">
                          {t.description}
                        </div>
                      )}
                      <ShareAnalyticsRow t={t} />
                    </button>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyShareUrl(t); }}
                        className="rounded-md p-1 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-fuchsia-50 hover:text-fuchsia-600 transition-opacity"
                        title="Copy public demo URL (auto-applies story + magic-link logs in)"
                        data-testid={`dd-template-share-${t.id}`}
                      >
                        <Link2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); downloadCalendarInvite(t); }}
                        className="rounded-md p-1 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-indigo-50 hover:text-indigo-600 transition-opacity"
                        title="Download calendar invite (.ics) with the share URL embedded"
                        data-testid={`dd-template-calendar-${t.id}`}
                      >
                        <CalendarPlus className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); revokeShareUrl(t); }}
                        className="rounded-md p-1 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-amber-50 hover:text-amber-600 transition-opacity"
                        title="Revoke + rotate share URL"
                        data-testid={`dd-template-revoke-${t.id}`}
                      >
                        <RotateCw className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }}
                        className="rounded-md p-1 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-600 transition-opacity"
                        title="Delete template"
                        data-testid={`dd-template-delete-${t.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
          <TabsList className="mx-6 mt-3 self-start" data-testid="dd-tabs-list">
            <TabsTrigger value="preview" data-testid="dd-tab-preview">Preview</TabsTrigger>
            <TabsTrigger value="insights" data-testid="dd-tab-insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="mt-3 focus-visible:outline-none">
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] divide-x divide-slate-100">
          {/* ========== LEFT: Knob panel ========== */}
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto bg-slate-50/50">
            {!catalog ? (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                {/* Industry preset */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    Industry preset
                  </Label>
                  <Select
                    value={config.industry_preset}
                    onValueChange={(v) => setKnob('industry_preset', v)}
                  >
                    <SelectTrigger
                      className="h-9 text-xs"
                      data-testid="dd-industry-preset-trigger"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalog.industry_preset_options.map((opt) => (
                        <SelectItem
                          key={opt.id}
                          value={opt.id}
                          data-testid={`dd-industry-${opt.id}`}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Final band target */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    Final maturity band (Q1 2026)
                  </Label>
                  <RadioGroup
                    value={config.final_band_target}
                    onValueChange={(v) => setKnob('final_band_target', v)}
                    className="grid grid-cols-3 gap-1"
                  >
                    {catalog.final_band_target_options.map((b) => (
                      <label
                        key={b}
                        htmlFor={`dd-band-${b}`}
                        className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-medium cursor-pointer transition-colors ${
                          config.final_band_target === b
                            ? 'border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <RadioGroupItem
                          id={`dd-band-${b}`}
                          value={b}
                          className="sr-only"
                          data-testid={`dd-band-${b}`}
                        />
                        {b}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Dip severity */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    Dip severity (Q3 2025)
                  </Label>
                  <RadioGroup
                    value={config.dip_severity}
                    onValueChange={(v) => setKnob('dip_severity', v)}
                    className="grid grid-cols-3 gap-1"
                  >
                    {catalog.dip_severity_options.map((d) => (
                      <label
                        key={d}
                        htmlFor={`dd-dip-${d}`}
                        className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-medium cursor-pointer capitalize transition-colors ${
                          config.dip_severity === d
                            ? 'border-rose-400 bg-rose-50 text-rose-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <RadioGroupItem
                          id={`dd-dip-${d}`}
                          value={d}
                          className="sr-only"
                          data-testid={`dd-dip-${d}`}
                        />
                        {d}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Recovery pace */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    Recovery pace (Q4 2025 → Q1 2026)
                  </Label>
                  <RadioGroup
                    value={config.recovery_pace}
                    onValueChange={(v) => setKnob('recovery_pace', v)}
                    className="grid grid-cols-3 gap-1"
                  >
                    {catalog.recovery_pace_options.map((r) => (
                      <label
                        key={r}
                        htmlFor={`dd-rec-${r}`}
                        className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-medium cursor-pointer capitalize transition-colors ${
                          config.recovery_pace === r
                            ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <RadioGroupItem
                          id={`dd-rec-${r}`}
                          value={r}
                          className="sr-only"
                          data-testid={`dd-rec-${r}`}
                        />
                        {r}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Primary constraint pillar */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    Primary constraint (lagging pillar)
                  </Label>
                  <Select
                    value={config.primary_constraint_pillar}
                    onValueChange={(v) => setKnob('primary_constraint_pillar', v)}
                  >
                    <SelectTrigger
                      className="h-9 text-xs"
                      data-testid="dd-pillar-trigger"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalog.primary_constraint_pillar_options.map((p) => (
                        <SelectItem
                          key={p.id}
                          value={p.id}
                          data-testid={`dd-pillar-${p.id}`}
                        >
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Constraint intensity (knob v2) */}
                {catalog.constraint_intensity_options && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      Constraint intensity
                    </Label>
                    <RadioGroup
                      value={config.constraint_intensity}
                      onValueChange={(v) => setKnob('constraint_intensity', v)}
                      className="grid grid-cols-3 gap-1"
                    >
                      {catalog.constraint_intensity_options.map((c) => (
                        <label
                          key={c}
                          htmlFor={`dd-cint-${c}`}
                          className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-medium cursor-pointer capitalize transition-colors ${
                            config.constraint_intensity === c
                              ? 'border-orange-400 bg-orange-50 text-orange-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          <RadioGroupItem
                            id={`dd-cint-${c}`}
                            value={c}
                            className="sr-only"
                            data-testid={`dd-cint-${c}`}
                          />
                          {c}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Benchmark percentile (knob v2) */}
                {catalog.benchmark_percentile_options && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      Benchmark vs peers
                    </Label>
                    <RadioGroup
                      value={config.benchmark_percentile}
                      onValueChange={(v) => setKnob('benchmark_percentile', v)}
                      className="grid grid-cols-3 gap-1"
                    >
                      {catalog.benchmark_percentile_options.map((p) => (
                        <label
                          key={p.id}
                          htmlFor={`dd-bench-${p.id}`}
                          className={`flex flex-col items-center gap-0.5 rounded-md border px-2 py-2 text-[11px] font-medium cursor-pointer transition-colors ${
                            config.benchmark_percentile === p.id
                              ? 'border-cyan-400 bg-cyan-50 text-cyan-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                          }`}
                          title={p.description}
                        >
                          <RadioGroupItem
                            id={`dd-bench-${p.id}`}
                            value={p.id}
                            className="sr-only"
                            data-testid={`dd-bench-${p.id}`}
                          />
                          {p.label}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Seed */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    RNG seed
                  </Label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={9999999}
                      value={config.seed}
                      onChange={(e) => setKnob('seed', Number(e.target.value) || 0)}
                      className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-xs focus:border-fuchsia-400 focus:outline-none"
                      data-testid="dd-seed-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={reroll}
                      className="h-9 w-9 text-fuchsia-600 hover:bg-fuchsia-50"
                      data-testid="dd-seed-reroll-btn"
                      title="Re-roll seed for fresh-looking jitter"
                    >
                      <Dice5 className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 leading-tight">
                    Same seed = same dataset. Re-roll to get fresh organic noise.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* ========== RIGHT: Preview panel ========== */}
          <div
            ref={previewPanelRef}
            className="p-5 space-y-4 max-h-[70vh] overflow-y-auto bg-white"
          >
            {/* Headline strip */}
            {preview && (
              <div
                className="grid grid-cols-3 gap-3 rounded-lg border border-slate-100 bg-gradient-to-br from-fuchsia-50/40 to-indigo-50/40 p-3"
                data-testid="dd-headline-strip"
              >
                <Headline label="Industry" value={preview.headline.industry} />
                <Headline
                  label="Final band"
                  value={`${preview.headline.final_band}`}
                  sub={`${preview.headline.final_maturity_score} pts`}
                />
                <Headline
                  label="Constraint"
                  value={preview.headline.primary_constraint_pillar_label}
                />
              </div>
            )}

            {/* GPT-5.2 cover-slide copy */}
            {preview && (
              <div
                className="rounded-lg border border-fuchsia-100 bg-gradient-to-br from-white to-fuchsia-50/40 p-4"
                data-testid="dd-tagline-block"
              >
                {tagline ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Quote className="h-4 w-4 text-fuchsia-500 flex-none mt-0.5" />
                      <div className="flex-1">
                        <p
                          className="text-sm font-semibold leading-snug text-slate-800 whitespace-pre-line"
                          data-testid="dd-tagline-text"
                        >
                          {tagline.tagline}
                        </p>
                        {tagline.subheadline && (
                          <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">
                            {tagline.subheadline}
                          </p>
                        )}
                      </div>
                    </div>
                    {Array.isArray(tagline.headline_kpis) && tagline.headline_kpis.length > 0 && (
                      <div
                        className="grid grid-cols-3 gap-2"
                        data-testid="dd-tagline-kpis"
                      >
                        {tagline.headline_kpis.map((k, i) => (
                          <div
                            key={i}
                            className="rounded-md bg-white border border-slate-100 px-2.5 py-2"
                          >
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate" title={k.label}>
                              {k.label}
                            </div>
                            <div className="text-sm font-bold text-slate-800 leading-tight">{k.value}</div>
                            {k.delta && (
                              <div
                                className={`text-[11px] font-semibold ${
                                  isImprovementDelta(k)
                                    ? 'text-emerald-600'
                                    : 'text-rose-600'
                                }`}
                              >
                                {k.delta}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={generateTagline}
                    disabled={taglineLoading}
                    className="flex items-center gap-2 text-xs font-semibold text-fuchsia-700 hover:text-fuchsia-800 disabled:opacity-60"
                    data-testid="dd-generate-tagline-btn"
                  >
                    {taglineLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Generate cover-slide tagline (GPT-5.2)
                  </button>
                )}
              </div>
            )}

            {/* LEI overall sparkline */}
            <ChartCard
              title="LEI overall (avg of 15 measures)"
              subtitle="12-month story arc — dip → recovery → close"
              loading={loadingPreview && !preview}
            >
              {preview && (
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={leiChartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} domain={[40, 90]} />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="avg"
                      stroke="#a21caf"
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Maturity sparkline */}
            <ChartCard
              title="Maturity Trend"
              subtitle="7 historical assessments spread across ~14 months"
              loading={loadingPreview && !preview}
            >
              {preview && (
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={maturityChartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} domain={[20, 90]} />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="overall"
                      stroke="#0d9488"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: '#0d9488' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* KPI multi-line (normalised) */}
            <ChartCard
              title={`KPIs (normalised vs baseline)`}
              subtitle={preview ? `${preview.kpi_trends.length} ${preview.headline.industry} KPIs · 100 = baseline` : ''}
              loading={loadingPreview && !preview}
            >
              {preview && (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={kpiChartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                    {preview.kpi_trends.map((k, idx) => (
                      <Line
                        key={k.kpi_id}
                        type="monotone"
                        dataKey={k.kpi_id}
                        name={k.name}
                        stroke={[
                          '#0ea5e9', '#10b981', '#f59e0b',
                          '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899',
                        ][idx % 7]}
                        strokeWidth={1.8}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </div>
          </TabsContent>

          <TabsContent value="insights" className="mt-3 focus-visible:outline-none px-6 pb-4">
            <DemoDirectorInsights
              insights={insights}
              loading={insightsLoading}
              onRefresh={refreshInsights}
              batchReengageIds={batchReengageIds}
              onBatchReengageConsumed={onBatchReengageConsumed}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t border-slate-100 px-6 py-3 gap-2 bg-slate-50/50 flex-col sm:flex-col items-stretch">
          {saveOpen && (
            <div
              className="flex items-center gap-2 rounded-md border border-fuchsia-200 bg-white p-2"
              data-testid="dd-save-template-form"
            >
              <input
                autoFocus
                type="text"
                placeholder="Template name (e.g. Healthcare CHRO pitch)"
                value={saveForm.name}
                onChange={(e) => setSaveForm((f) => ({ ...f, name: e.target.value }))}
                className="flex-1 rounded border border-slate-200 px-2 py-1 text-[11px] focus:border-fuchsia-400 focus:outline-none"
                data-testid="dd-save-name-input"
              />
              <input
                type="text"
                placeholder="Tags (comma-separated)"
                value={saveForm.tags}
                onChange={(e) => setSaveForm((f) => ({ ...f, tags: e.target.value }))}
                className="w-44 rounded border border-slate-200 px-2 py-1 text-[11px] focus:border-fuchsia-400 focus:outline-none"
                data-testid="dd-save-tags-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={saveTemplate}
                disabled={saving || !saveForm.name.trim()}
                className="bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                data-testid="dd-save-confirm-btn"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                <span className="ml-1">Save</span>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setSaveOpen(false); setSaveForm({ name: '', description: '', tags: '' }); }}
                className="text-slate-500"
                data-testid="dd-save-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center text-[11px] text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 mr-1" />
              Apply will wipe + re-write Acme LEI / KPI / Maturity collections.
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSaveOpen(true)}
              disabled={saveOpen || !preview}
              data-testid="dd-save-template-btn"
              title="Save these knobs (and tagline if generated) as a reusable story template"
            >
              <Save className="h-4 w-4 mr-1.5 text-fuchsia-600" />
              Save template…
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={captureCoverSlide}
              disabled={capturing || !preview}
              data-testid="dd-capture-png-btn"
              title="Download the preview panel as a deck-ready cover slide (PNG)"
            >
              {capturing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}
              Capture PNG
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange?.(false)}
              data-testid="dd-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={apply}
              disabled={applying || !preview}
              className="bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white hover:opacity-90"
              data-testid="dd-apply-btn"
            >
              {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Apply story to Acme
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Confirmation dialog — replaces window.confirm for delete /
          revoke / apply-to-Acme. Single dialog driven by `confirmAction`. */}
      <AlertDialog
        open={Boolean(confirmAction)}
        onOpenChange={(o) => { if (!o) setConfirmAction(null); }}
      >
        <AlertDialogContent data-testid="dd-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {confirmAction?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dd-confirm-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const action = confirmAction;
                setConfirmAction(null);
                try { await action?.onConfirm?.(); } catch (_e) { /* handled inline */ }
              }}
              className={confirmAction?.danger
                ? 'bg-rose-600 text-white hover:bg-rose-700'
                : 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'
              }
              data-testid="dd-confirm-action-btn"
            >
              {confirmAction?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

const Headline = ({ label, value, sub }) => (
  <div className="flex flex-col">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-800 leading-tight">{value}</span>
    {sub && <span className="text-[10px] text-slate-500">{sub}</span>}
  </div>
);

/**
 * Inline share-URL analytics for a saved template:
 * • not-shared yet (no share_token)        → "Not shared"          (slate)
 * • shared but never opened                → "Share URL active — never opened" (amber)
 * • shared + at least one prospect opened  → "Opened Nx · last 3d ago"          (emerald)
 *
 * Driven by data already present on each template doc — no extra fetch.
 */
const ShareAnalyticsRow = ({ t }) => {
  const opens = t.share_open_count || 0;
  const hasShareToken = Boolean(t.share_token);
  const lastOpenAt = t.last_share_open_at;

  let icon = <EyeOff className="h-2.5 w-2.5" />;
  let text = 'Not shared';
  let toneClass = 'text-slate-400';

  if (hasShareToken && opens === 0) {
    icon = <Eye className="h-2.5 w-2.5" />;
    text = 'Share URL active — never opened';
    toneClass = 'text-amber-600';
  } else if (opens > 0) {
    icon = <Eye className="h-2.5 w-2.5" />;
    let when = '';
    if (lastOpenAt) {
      try {
        const d = typeof lastOpenAt === 'string' ? parseISO(lastOpenAt) : new Date(lastOpenAt);
        when = ` · last ${formatDistanceToNow(d, { addSuffix: false })} ago`;
      } catch (_e) { /* ignore parse errors */ }
    }
    text = `Opened ${opens}×${when}`;
    toneClass = 'text-emerald-600';
  }

  return (
    <div
      className={`mt-1 flex items-center gap-1 text-[10px] font-medium ${toneClass}`}
      data-testid={`dd-template-share-analytics-${t.id}`}
    >
      {icon}
      <span className="truncate">{text}</span>
    </div>
  );
};

// "Lower is better" KPI labels — when these show a negative delta it's an
// improvement; when they show positive, it's a regression.
const LOWER_IS_BETTER_HINTS = /safety|incident|defect|error|cost|readmission|time|lead time|onboarding|finding/i;

function isImprovementDelta(kpi) {
  const raw = String(kpi.delta || '').trim();
  if (!raw) return true;
  const negative = raw.startsWith('-') || raw.startsWith('−');
  const lowerBetter = LOWER_IS_BETTER_HINTS.test(kpi.label || '');
  // Improvement when sign matches direction:
  //   higher-is-better KPI + positive delta → green
  //   lower-is-better KPI  + negative delta → green
  return lowerBetter ? negative : !negative;
}

const ChartCard = ({ title, subtitle, loading, children }) => (
  <div className="rounded-lg border border-slate-100 bg-white p-3">
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <div>
        <h4 className="text-xs font-semibold text-slate-700">{title}</h4>
        {subtitle && <p className="text-[10px] text-slate-400">{subtitle}</p>}
      </div>
      {loading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
    </div>
    {children || (
      <div className="h-32 flex items-center justify-center text-[11px] text-slate-400">
        Adjust knobs to preview
      </div>
    )}
  </div>
);

/**
 * Aggregate analytics across all saved templates — extracted to
 * `./DemoDirectorInsights.jsx`. Re-exported for callers that imported
 * from this file historically.
 */
export { DemoDirectorInsights as InsightsPanel, InsightStat } from './DemoDirectorInsights';

export default DemoDirector;
